import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Bell, X } from "lucide-react";
import { authFetch } from "@/lib/authFetch";

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) output[i] = rawData.charCodeAt(i);
  return output;
}

export function PushNotificationsManager() {
  const [permission, setPermission] = useState<NotificationPermission | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (typeof Notification === "undefined") return;
    setPermission(Notification.permission);
    if (localStorage.getItem("push-dismissed") === "1") setDismissed(true);
    if (localStorage.getItem("push-subscribed") === "1") setSubscribed(true);
  }, []);

  const handleEnable = async () => {
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") { setLoading(false); return; }

      const { publicKey } = await authFetch("/api/push/vapid-public-key").then((r) => r.json());
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      const subJson = subscription.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
      await authFetch("/api/push/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys,
          deviceLabel: navigator.userAgent.slice(0, 100),
        }),
      });
      setSubscribed(true);
      localStorage.setItem("push-subscribed", "1");
    } catch (e) {
      console.error("[PushManager] subscribe failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem("push-dismissed", "1");
  };

  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
  if (permission === "granted" || permission === "denied") return null;
  if (dismissed || subscribed || !permission) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100vw-2rem)] max-w-sm bg-card border border-border rounded-xl shadow-xl p-4 flex items-start gap-3 animate-in slide-in-from-bottom-4">
      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
        <Bell className="w-4 h-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground">Activer les notifications</p>
        <p className="text-xs text-muted-foreground mt-0.5">Soyez alerté des nouveaux messages en temps réel</p>
        <div className="flex gap-2 mt-3">
          <Button size="sm" className="h-7 text-xs" onClick={handleEnable} disabled={loading}>
            {loading ? "…" : "Activer"}
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={handleDismiss}>
            Plus tard
          </Button>
        </div>
      </div>
      <button onClick={handleDismiss} className="p-1 text-muted-foreground hover:text-foreground transition-colors shrink-0">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
